// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

function parseIso(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function isValidTimezone(timezone: string | undefined): boolean {
  if (!timezone) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function getResolvedTimezone(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(timezone) ? timezone : 'UTC';
  } catch {
    return 'UTC';
  }
}

export function getTimezoneAbbreviation(iso: string | undefined, timezone = getResolvedTimezone()): string {
  const date = parseIso(iso);
  const safeTimezone = isValidTimezone(timezone) ? timezone : 'UTC';

  if (!date) {
    return '';
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimezone,
      hour: 'numeric',
      timeZoneName: 'short',
    }).formatToParts(date);

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return safeTimezone === 'UTC' ? 'UTC' : '';
  }
}

export function formatAppointmentRangeInTimezone(
  start: string | undefined,
  end: string | undefined,
  timezone: string,
  options?: {
    includeDate?: boolean;
    includeTimezone?: boolean;
  }
): string {
  const startDate = parseIso(start);
  const endDate = parseIso(end);
  const safeTimezone = isValidTimezone(timezone) ? timezone : 'UTC';

  if (!startDate || !endDate) {
    return '—';
  }

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: safeTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: safeTimezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const startDateLabel = dateFormatter.format(startDate);
  const endDateLabel = dateFormatter.format(endDate);
  const startTimeLabel = timeFormatter.format(startDate).replace(' ', '');
  const endTimeLabel = timeFormatter.format(endDate).replace(' ', '');
  const timezoneLabel = getTimezoneAbbreviation(start, safeTimezone) || safeTimezone;

  const parts: string[] = [];

  if (options?.includeDate !== false) {
    parts.push(startDateLabel === endDateLabel ? startDateLabel : `${startDateLabel}–${endDateLabel}`);
  }

  parts.push(`${startTimeLabel}–${endTimeLabel}`);

  if (options?.includeTimezone !== false) {
    parts.push(timezoneLabel);
  }

  return parts.join(' ');
}
